import { env } from '../config/env'
import { setUserContext } from '../instrument'

export interface AuthSession {
  token: string
  providerId: string
  locationId: string
  email?: string
  userName?: string
  role?: string
  providerName?: string
  locationName?: string
  locationAddress?: string
}

const STORAGE_KEY = 'filazero.authSession.v1'
export const SESSION_EXPIRED_MESSAGE = 'Sua sessão expirou. Entre novamente para continuar.'

// Sessão automática do modo mock (demo offline): não há backend para autenticar,
// então pulamos o AuthFlow. Valores coerentes com os seeds do MockDataSource.
const MOCK_SESSION: AuthSession = {
  token: 'mock',
  providerId: '3440',
  locationId: '1',
  email: 'demo@filazero.com',
  userName: 'Operador Demo',
  role: 'owner',
  providerName: 'Filazero Demo',
  locationName: 'Unidade Central',
}

let runtimeSession: AuthSession | null = null
let sessionExpired = false
const expirationListeners = new Set<() => void>()

export function onAuthSessionExpired(listener: () => void): () => void {
  expirationListeners.add(listener)
  return () => { expirationListeners.delete(listener) }
}

export function expireAuthSession(requestToken: string | undefined): void {
  // Uma resposta de uma requisição antiga não pode encerrar um login novo.
  if (!requestToken || requestToken !== getApiSession().token) { return }
  clearAuthSession()
  sessionExpired = true
  expirationListeners.forEach((listener) => listener())
}

export function cleanBearerToken(token: string): string {
  return token.trim().replace(/^Bearer\s+/i, '')
}

export function getEnvSessionDefaults(): Partial<AuthSession> {
  return {
    token: env.authToken ?? '',
    providerId: env.providerId,
    locationId: env.locationId,
  }
}

export function loadAuthSession(): AuthSession | null {
  if (sessionExpired) { return null }
  if (runtimeSession) { return runtimeSession }
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw) as AuthSession
      if (parsed.token && parsed.providerId && parsed.locationId) {
        runtimeSession = { ...parsed, token: cleanBearerToken(parsed.token) }

        // Update Sentry user context for loaded session
        setUserContext({
          id: runtimeSession.providerId,
          email: runtimeSession.email,
          username: runtimeSession.userName,
          data: {
            providerId: runtimeSession.providerId,
            locationId: runtimeSession.locationId,
            providerName: runtimeSession.providerName,
            locationName: runtimeSession.locationName,
            role: runtimeSession.role,
          },
        })

        return runtimeSession
      }
    }
  } catch {
    /* segue para o fallback */
  }
  // Modo mock: entra direto, sem login (demo offline).
  if (env.dataSource === 'mock') {
    runtimeSession = MOCK_SESSION

    // Update Sentry user context for mock session
    setUserContext({
      id: MOCK_SESSION.providerId,
      email: MOCK_SESSION.email,
      username: MOCK_SESSION.userName,
      data: {
        providerId: MOCK_SESSION.providerId,
        locationId: MOCK_SESSION.locationId,
        providerName: MOCK_SESSION.providerName,
        locationName: MOCK_SESSION.locationName,
        role: MOCK_SESSION.role,
      },
    })

    return runtimeSession
  }
  return null
}

export function saveAuthSession(session: AuthSession): AuthSession {
  sessionExpired = false
  runtimeSession = { ...session, token: cleanBearerToken(session.token) }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(runtimeSession))

  // Update Sentry user context
  setUserContext({
    id: session.providerId,
    email: session.email,
    username: session.userName,
    data: {
      providerId: session.providerId,
      locationId: session.locationId,
      providerName: session.providerName,
      locationName: session.locationName,
      role: session.role,
    },
  })

  return runtimeSession
}

export function clearAuthSession(): void {
  runtimeSession = null
  sessionExpired = false
  try { localStorage.removeItem(STORAGE_KEY) } catch { /* storage indisponível */ }

  // Clear Sentry user context
  setUserContext(null)
}

export function getApiSession(): Partial<AuthSession> {
  // Não reutiliza VITE_AUTH_TOKEN depois de a API invalidar a sessão.
  if (sessionExpired) { return {} }
  return runtimeSession ?? loadAuthSession() ?? getEnvSessionDefaults()
}
